import React, { useEffect, useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';

import { Button, Checkbox, Input, Slider, cn } from '@design-system';

import { useCustomAmountInput } from '../hooks/use-custom-amount-input';

export interface CustomAmountInputProps {
  name: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  updateClient?: (value: number) => void;
  formatValue?: (value: number) => string | number;
  testId?: string;
  inputLabel?: string;
  inputId?: string;
}

export function CustomAmountInput({
  name,
  value,
  min,
  max,
  onChange,
  updateClient,
  formatValue,
  testId,
  inputLabel = 'Custom Amount:',
  inputId,
}: CustomAmountInputProps) {
  const { draft, rangeNotice, handleChange, handleBlur, handleFocus } = useCustomAmountInput({
    value,
    min,
    max,
    onChange,
    updateClient,
  });

  const format = (amount: number) => (formatValue ? String(formatValue(amount)) : String(amount));
  const noticeId = `${name}-custom-input-notice`;
  const resolvedInputId = inputId ?? `${name}-custom-input`;
  const noticeMessage =
    rangeNotice === 'below'
      ? `Lowest available amount is ${format(min)}`
      : rangeNotice === 'above'
        ? `Highest available amount is ${format(max)}`
        : null;

  return (
    <div className="space-y-1.5">
      <div className="flex w-full items-center gap-3">
        <label
          htmlFor={resolvedInputId}
          className="text-foreground shrink-0 text-sm font-medium"
        >
          {inputLabel}
        </label>
        <Input
          id={resolvedInputId}
          type="number"
          min={min}
          max={max}
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          className="h-9 min-w-0 flex-1"
          placeholder={format(min)}
          aria-describedby={noticeMessage ? noticeId : undefined}
          aria-invalid={noticeMessage ? true : undefined}
          data-testid={`${testId}-custom-input`}
        />
      </div>
      {noticeMessage && (
        <p
          id={noticeId}
          role="status"
          className="text-destructive text-xs"
          data-testid={`${testId}-custom-input-notice`}
        >
          {noticeMessage}
        </p>
      )}
    </div>
  );
}

function SliderTooltip({
  isHovered,
  value,
  position,
  suffix = '',
}: {
  isHovered: boolean;
  value: number | string;
  position: number;
  isDefault?: boolean;
  suffix?: string;
}) {
  return (
    <div
      className={cn(
        'pointer-events-none absolute z-50 -mt-10 whitespace-nowrap rounded-md bg-black px-3 py-1.5 text-xs text-white opacity-0 transition-opacity',
        {
          'opacity-100': isHovered,
        },
      )}
      style={{
        left: `${position}%`,
        transform: `translateX(-${position}%)`,
      }}
    >
      <span className="font-semibold">
        {value}
        {suffix}
      </span>
    </div>
  );
}

interface CustomSliderProps {
  'name': string;
  'control': Control<any>;
  'min': number;
  'max': number;
  'step'?: number;
  'defaultValue': number;
  'formatValue'?: (value: number) => string | number;
  'updateClient'?: (value: number) => void;
  'labelSuffix'?: string;
  'defaultLabel'?: string;
  'hideResetButton'?: boolean;
  /**
   * Pass a function to have the label follow the live drag value; a plain node only
   * updates when the committed form value changes.
   */
  'label'?: React.ReactNode | ((value: number) => React.ReactNode);
  'data-testid'?: string;
  'showCustomInput'?: boolean;
  'customInputLabel'?: string;
  'defaultCustomInputChecked'?: boolean;
  'onCustomInputChange'?: (checked: boolean) => void;
  'showMinMax'?: boolean;
  'minMaxSuffix'?: string;
  'headerActions'?: React.ReactNode;
}

export function CustomSlider({
  name,
  control,
  min,
  max,
  step = 1,
  defaultValue,
  formatValue,
  updateClient,
  labelSuffix = '',
  defaultLabel = 'Default',
  hideResetButton = false,
  label,
  'data-testid': testId,
  showCustomInput = false,
  customInputLabel = 'Enter custom amount',
  defaultCustomInputChecked = false,
  onCustomInputChange,
  showMinMax = false,
  minMaxSuffix = '',
  headerActions,
}: CustomSliderProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showCustomInputField, setShowCustomInputField] = useState(defaultCustomInputChecked);
  // Held only while the thumb is being dragged. Keeping the in-flight value out of the
  // form means a drag costs nothing beyond this component: no downstream filtering runs
  // until the user lets go.
  const [dragValue, setDragValue] = useState<number | null>(null);
  const latestDragValueRef = useRef<number | null>(null);
  const pointerReleaseHandledRef = useRef(false);

  useEffect(() => {
    setShowCustomInputField(defaultCustomInputChecked);
  }, [defaultCustomInputChecked]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const displayValue = dragValue ?? value;
        const isDefault = displayValue === defaultValue;
        const resolvedLabel = typeof label === 'function' ? label(displayValue) : label;

        function handleReset(event: React.MouseEvent) {
          event.stopPropagation();
          event.preventDefault();

          latestDragValueRef.current = null;
          pointerReleaseHandledRef.current = false;
          setDragValue(null);
          onChange(defaultValue);
          if (updateClient) {
            updateClient(defaultValue);
          }
        }

        function commitValue(nextValue: number) {
          latestDragValueRef.current = null;
          setDragValue(null);
          onChange(nextValue);
          if (updateClient) {
            updateClient(nextValue);
          }
        }

        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {resolvedLabel && (
                <div className="text-primary-dark text-lg font-semibold">{resolvedLabel}</div>
              )}
              <div className="ml-auto flex items-center gap-2">
                {headerActions}
                {!isDefault && !hideResetButton && (
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                  >
                    {defaultLabel}
                  </Button>
                )}
              </div>
            </div>
            <div className={showMinMax ? 'flex items-center gap-3' : 'relative'}>
              {showMinMax && (
                <span className="text-sm text-gray-400">
                  {min}
                  {minMaxSuffix}
                </span>
              )}
              <div className="relative flex-1">
                <Slider
                  value={[displayValue]}
                  onValueChange={newValues => {
                    latestDragValueRef.current = newValues[0];
                    pointerReleaseHandledRef.current = false;
                    setDragValue(newValues[0]);
                  }}
                  onValueCommit={newValues => {
                    // Radix calls our onPointerUp before its own slide-end handler. If the
                    // release already handled the latest drag value, ignore the ensuing
                    // callback even when Radix reports a stale controlled value.
                    if (pointerReleaseHandledRef.current) {
                      pointerReleaseHandledRef.current = false;
                      return;
                    }

                    commitValue(newValues[0]);
                  }}
                  onPointerUp={() => {
                    const latestDragValue = latestDragValueRef.current;
                    if (latestDragValue === null) {
                      pointerReleaseHandledRef.current = false;
                      setDragValue(null);
                      return;
                    }

                    if (latestDragValue === value) {
                      latestDragValueRef.current = null;
                      pointerReleaseHandledRef.current = false;
                      setDragValue(null);
                      return;
                    }

                    pointerReleaseHandledRef.current = true;
                    // Radix runs its slide-end handler synchronously after this handler.
                    // Keep suppression scoped to that one event so a missing Radix commit
                    // cannot swallow a later keyboard commit.
                    queueMicrotask(() => {
                      pointerReleaseHandledRef.current = false;
                    });
                    commitValue(latestDragValue);
                  }}
                  onPointerCancel={() => {
                    latestDragValueRef.current = null;
                    pointerReleaseHandledRef.current = false;
                    setDragValue(null);
                  }}
                  min={min}
                  max={max}
                  step={step}
                  className="**:data-[slot=slider-thumb]:size-4 **:data-[slot=slider-track]:h-1.5"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  data-testid={testId}
                />

                <SliderTooltip
                  isHovered={isHovered}
                  value={formatValue ? formatValue(displayValue) : displayValue}
                  position={((displayValue - min) / (max - min)) * 100}
                  isDefault={isDefault}
                  suffix={labelSuffix}
                />
              </div>
              {showMinMax && (
                <span className="text-sm text-gray-400">
                  {max}
                  {minMaxSuffix}
                </span>
              )}
            </div>
            {showCustomInput && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${name}-show-custom-amount`}
                    checked={showCustomInputField}
                    onCheckedChange={checked => {
                      const val = checked === true;
                      setShowCustomInputField(val);
                      onCustomInputChange?.(val);
                    }}
                  />
                  <label
                    htmlFor={`${name}-show-custom-amount`}
                    className="text-muted-foreground cursor-pointer text-sm"
                  >
                    {customInputLabel}
                  </label>
                </div>
                {showCustomInputField && (
                  <CustomAmountInput
                    name={name}
                    value={displayValue}
                    min={min}
                    max={max}
                    onChange={onChange}
                    updateClient={updateClient}
                    formatValue={formatValue}
                    testId={testId}
                  />
                )}
              </>
            )}
          </div>
        );
      }}
    />
  );
}
